/**
 * Signal Engine (§19, §20, §21 + §26 confluence alerts)
 *
 * Optional strategy mode: LONG/SHORT setups composed from narrow CPR,
 * opening position, close beyond the CPR, VWAP/volume/structure confirm
 * and the confluence score ≥ minimum. All conditions are evaluated on
 * CLOSED bars only (entry = breakout candle close), once per session.
 *
 * Also owns the per-session "Bullish/Bearish Confluence" alert (keyed by
 * session start so it can only fire once per qualifying session).
 */

import { CandleData } from "../../../types/chart.types";
import { IndicatorMarker } from "../../types/indicator.types";
import {
  CprAlertEvent, ConfluenceScore, CprCore, PivotLevels, PositionType,
  SessionAgg, SetupInfo, SlSource, TpSource, WidthType,
} from "./types";
import { classifyBias, priceVsCpr } from "./classification";
import { confluenceScore, ScoreContext } from "./confluence";
import { atrAt, buildSessionVwap, marketStructure } from "./market-context";
import { volumeSma } from "./breakout";

const ATR_PERIOD = 14;
const SWING_LOOKBACK = 10;

export interface SignalParams {
  showSetups: boolean;
  useVwap: boolean;
  useVolume: boolean;
  useStructure: boolean;
  minScore: number;
  longSlSource: SlSource;
  shortSlSource: SlSource;
  longTpSource: TpSource;
  shortTpSource: TpSource;
  rrMultiple: number;
  slAtrMult: number;
  tpAtrMult: number;
}

export interface SignalOutput {
  markers: IndicatorMarker[];
  alerts: CprAlertEvent[];
  setup: SetupInfo | null;
  /** §21 — wide CPR + ranging regime → range-style technical view only */
  rangeActive: boolean;
  /** confluence score measured at the last closed bar */
  scoreAtClose: ConfluenceScore | null;
}

/** Lowest low / highest high of the `SWING_LOOKBACK` bars before `idx`. */
const prevSwingLow = (candles: CandleData[], idx: number): number | null => {
  const start = Math.max(0, idx - SWING_LOOKBACK);
  if (start >= idx) return null;
  let lo = Infinity;
  for (let i = start; i < idx; i++) if (candles[i].low < lo) lo = candles[i].low;
  return isFinite(lo) ? lo : null;
};

const prevSwingHigh = (candles: CandleData[], idx: number): number | null => {
  const start = Math.max(0, idx - SWING_LOOKBACK);
  if (start >= idx) return null;
  let hi = -Infinity;
  for (let i = start; i < idx; i++) if (candles[i].high > hi) hi = candles[i].high;
  return isFinite(hi) ? hi : null;
};

interface SlTpArgs {
  side: "LONG" | "SHORT";
  entry: number;
  slSource: SlSource;
  tpSource: TpSource;
  cpr: CprCore;
  levels: PivotLevels;
  candles: CandleData[];
  idx: number;
  rrMultiple: number;
  slAtrMult: number;
  tpAtrMult: number;
}

/** Resolve concrete SL/TP numbers for a setup (§19/§20). */
const resolveSlTp = (a: SlTpArgs): { sl: number; tp: number } => {
  const atr = atrAt(a.candles, a.idx, ATR_PERIOD);
  const long = a.side === "LONG";

  let sl: number;
  switch (a.slSource) {
    case "CPR Top": sl = a.cpr.top; break;
    case "CPR Bottom": sl = a.cpr.bottom; break;
    case "ATR": sl = long ? a.entry - a.slAtrMult * atr : a.entry + a.slAtrMult * atr; break;
    case "Previous Swing High": {
      const sw = prevSwingHigh(a.candles, a.idx);
      sl = sw != null ? sw : long ? a.cpr.bottom : a.cpr.top;
      break;
    }
    case "Previous Swing Low":
    default: {
      const sw = prevSwingLow(a.candles, a.idx);
      sl = sw != null ? sw : long ? a.cpr.bottom : a.cpr.top;
      break;
    }
  }

  let tp: number;
  switch (a.tpSource) {
    case "R2": tp = a.levels.r2; break;
    case "R3": tp = a.levels.r3; break;
    case "S2": tp = a.levels.s2; break;
    case "S3": tp = a.levels.s3; break;
    case "R1": case "S1": tp = long ? a.levels.r1 : a.levels.s1; break;
    case "Fixed RR": {
      const risk = Math.abs(a.entry - sl);
      tp = long ? a.entry + a.rrMultiple * risk : a.entry - a.rrMultiple * risk;
      break;
    }
    case "ATR Multiple":
    default:
      tp = long ? a.entry + a.tpAtrMult * atr : a.entry - a.tpAtrMult * atr;
      break;
  }
  return { sl, tp };
};

export const runSignalEngine = (opts: {
  candles: CandleData[];
  session: SessionAgg;
  lastClosedIdx: number;
  cpr: CprCore;
  levels: PivotLevels;
  widthType: WidthType;
  position: PositionType;
  regime: string;
  params: SignalParams;
}): SignalOutput => {
  const { candles, session, lastClosedIdx, cpr, levels, widthType, position, regime, params } = opts;

  const markers: IndicatorMarker[] = [];
  const alerts: CprAlertEvent[] = [];
  let setup: SetupInfo | null = null;
  let longFired = false;
  let shortFired = false;
  let confluenceAlerted = false;

  const rangeActive = widthType === "WIDE" && regime === "RANGING";
  const narrow = widthType === "NARROW";
  const sessionOpen = session.open;
  const openLong = sessionOpen > cpr.top;
  const openShort = sessionOpen < cpr.bottom;

  const vwap = buildSessionVwap(candles, session);

  /** Score context at closed bar i (all live inputs of §18). */
  const ctxAt = (i: number): { ctx: ScoreContext; direction: "BULLISH" | "BEARISH" | "NEUTRAL" } => {
    const close = candles[i].close;
    const v = vwap.get(i);
    const vwapState: "ABOVE" | "BELOW" | "N/A" =
      v == null ? "N/A" : close >= v ? "ABOVE" : "BELOW";
    const vsCpr = priceVsCpr(close, cpr);
    const structure = marketStructure(candles, i, 20);
    const direction = classifyBias(
      position, vsCpr, params.useVwap ? vwapState : "N/A",
    );
    return { ctx: { widthType, position, vwap, structure, vwapState, vsCpr }, direction };
  };

  // Score for the dashboard at the last closed bar
  let scoreAtClose: ConfluenceScore | null = null;
  if (lastClosedIdx >= session.firstIdx) {
    const { ctx, direction } = ctxAt(lastClosedIdx);
    scoreAtClose = confluenceScore(candles, lastClosedIdx, ctx, direction);
  }

  const end = Math.min(session.lastIdx, lastClosedIdx);
  for (let i = session.firstIdx; i <= end; i++) {
    const c = candles[i];
    const close = c.close;
    const { ctx, direction } = ctxAt(i);

    // ── confluence alert (§26): fires once per session, stable key ──
    if (!confluenceAlerted && direction !== "NEUTRAL") {
      const s = confluenceScore(candles, i, ctx, direction);
      if (s.total >= params.minScore) {
        confluenceAlerted = true;
        alerts.push({
          time: session.start,
          event: direction === "BULLISH" ? "Bullish Confluence" : "Bearish Confluence",
          price: close,
          direction: direction === "BULLISH" ? "bull" : "bear",
        });
      }
    }

    // ── setup gating (§19/§20) ──
    if (!params.showSetups || !narrow) continue;

    const volOk = !params.useVolume || (i >= 19 ? c.volume > volumeSma(candles, i, 20) : true);
    const structure = ctx.structure;
    const v = vwap.get(i);
    const structOk = (st: string) => !params.useStructure || structure === st;
    const vwapOk = (dir: "bull" | "bear") => {
      if (!params.useVwap || v == null) return true;
      return dir === "bull" ? close > v : close < v;
    };
    const scoreOk = (dir: "bull" | "bear"): boolean => {
      const s = confluenceScore(
        candles, i, { ...ctx, vwapState: ctx.vwapState },
        dir === "bull" ? "BULLISH" : "BEARISH",
      );
      return s.total >= params.minScore;
    };

    if (!longFired && openLong && close > cpr.top &&
        volOk && structOk("BULLISH") && vwapOk("bull") && scoreOk("bull")) {
      longFired = true;
      const { sl, tp } = resolveSlTp({
        side: "LONG", entry: close, slSource: params.longSlSource,
        tpSource: params.longTpSource, cpr, levels, candles, idx: i,
        rrMultiple: params.rrMultiple, slAtrMult: params.slAtrMult, tpAtrMult: params.tpAtrMult,
      });
      setup = {
        side: "LONG", time: c.time, entry: close, sl, tp,
        slSource: params.longSlSource, tpSource: params.longTpSource,
      };
      markers.push({
        time: c.time, position: "belowBar", color: "#00B450", shape: "arrowUp",
        text: "LONG SETUP", size: 2,
      });
      alerts.push({ time: c.time, event: "Long Setup", price: close, direction: "bull" });
    }

    if (!shortFired && openShort && close < cpr.bottom &&
        volOk && structOk("BEARISH") && vwapOk("bear") && scoreOk("bear")) {
      shortFired = true;
      const { sl, tp } = resolveSlTp({
        side: "SHORT", entry: close, slSource: params.shortSlSource,
        tpSource: params.shortTpSource, cpr, levels, candles, idx: i,
        rrMultiple: params.rrMultiple, slAtrMult: params.slAtrMult, tpAtrMult: params.tpAtrMult,
      });
      setup = {
        side: "SHORT", time: c.time, entry: close, sl, tp,
        slSource: params.shortSlSource, tpSource: params.shortTpSource,
      };
      markers.push({
        time: c.time, position: "aboveBar", color: "#DC3232", shape: "arrowDown",
        text: "SHORT SETUP", size: 2,
      });
      alerts.push({ time: c.time, event: "Short Setup", price: close, direction: "bear" });
    }

    if (longFired && shortFired && confluenceAlerted) break;
  }

  return { markers, alerts, setup, rangeActive, scoreAtClose };
};
